import { ChangeDetectionStrategy, Component, input, output, TemplateRef } from '@angular/core';
import { AlertCardComponent, SectionDataComponent, SectionEmptyPreset } from '@presentation/components';
import { SkeletonPreset } from '@presentation/components/skeleton-loader';
import { type HeadingLevel } from '@presentation/directives';
import { SubHeaderLayout } from '../sub-header';

@Component({
  selector: 'cm-common-section-layout',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertCardComponent,
    SubHeaderLayout,
    SectionDataComponent,
  ],
  templateUrl: './common-section.layout.html',
  styleUrl: './common-section.layout.less',
})
export class CommonSectionLayout {
  // Section layout inputs
  public readonly title = input<string>('');
  public readonly titleLevel = input<HeadingLevel>('h1');
  public readonly visualTitleLevel = input<HeadingLevel>();
  public readonly description = input<string>('');
  public readonly descriptionHtml = input<string>('');
  public readonly headerDisclaimer = input<string>('');
  public readonly headerDisclaimerTitle = input<string>('');
  public readonly headerDisclaimerIcon = input<string>('error');
  public readonly collapsible = input<boolean>(false);
  public readonly disclaimerInitiallyExpanded = input<boolean>(false);
  public readonly overflowYDisabled = input<boolean>(false);

  // Sub-header back button inputs
  public readonly hasBackButton = input<boolean>(false);
  public readonly customBackHandler = input<boolean>(false);
  public readonly backButtonClick = output<void>();

  /** Opt-in breadcrumb, forwarded to the sub-header (off by default). */
  public readonly showBreadcrumb = input<boolean>(false);

  // SectionDataComponent inputs
  public readonly loading = input<boolean>(false);
  public readonly empty = input<boolean>(false);
  public readonly error = input<boolean>(false);
  public readonly skeletonLoaderPreset = input<SkeletonPreset>('section');

  /**
   * Optional custom loading template. When provided, it is rendered while `loading` is true instead
   * of the built-in skeleton preset — for pages that need a bespoke loading skeleton.
   */
  public readonly loadingTemplate = input<TemplateRef<unknown> | null>(null);

  public readonly emptyPreset = input<SectionEmptyPreset>('block');
  public readonly emptyIcon = input<string>('description');
  public readonly emptyTitle = input<string>('No data available');
  public readonly emptySubtitle = input<string>('');
  public readonly emptyButtonLabel = input<string>('');
  public readonly emptyButtonIcon = input<string>('add');
  public readonly emptyButtonLoading = input<boolean>(false);
  public readonly emptyUseCardContainer = input<boolean>(false);

  public readonly errorTitle = input<string>('Something went wrong');
  public readonly errorSubtitle = input<string>('Please try again later');
  public readonly errorButtonLabel = input<string>('Refresh');
  public readonly errorUseCardContainer = input<boolean>(false);

  // SectionDataComponent outputs
  public readonly emptyAction = output<void>();
  public readonly errorRefreshAction = output<void>();

  public onEmptyAction(): void {
    this.emptyAction.emit();
  }

  public onErrorRefreshAction(): void {
    this.errorRefreshAction.emit();
  }

  public onBackButtonClick(): void {
    this.backButtonClick.emit();
  }
}


